/**
 * Topic Video Player Component
 * Handles video playback for topic-level videos with presigned URL caching
 * and progress tracking for completion
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { videoService, TopicVideoRequest } from '../services/videoService';
import { trackClassroom, trackError } from '../services/analytics';
import { progressService } from '../services/progress.service';

interface TopicVideoPlayerProps {
  courseId: string;
  topicId: string;
  topicContent: string;
  autoplay?: boolean;
  // Progress tracking props
  enrollmentId?: string;
  segmentId?: string;
  lessonId?: string;
  moduleId?: string;
  isAlreadyCompleted?: boolean;
  onTopicCompleted?: (topicId: string, newProgressPercentage: number) => void;
}

const COMPLETION_THRESHOLD = 95;
const POSITION_SAVE_INTERVAL = 30000;

export const TopicVideoPlayer: React.FC<TopicVideoPlayerProps> = ({
  courseId,
  topicId,
  topicContent,
  autoplay = false,
  enrollmentId,
  segmentId,
  lessonId,
  moduleId,
  isAlreadyCompleted = false,
  onTopicCompleted,
}) => {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCompleted, setIsCompleted] = useState(isAlreadyCompleted);
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressTrackedRef = useRef({ 25: false, 50: false, 75: false, 100: false });
  const watchTimeRef = useRef(0);
  const lastTimeRef = useRef(0);
  const completionSentRef = useRef(isAlreadyCompleted);
  const lastPositionSaveRef = useRef(0);

  const progressTrackingEnabled = Boolean(enrollmentId && segmentId && lessonId && moduleId);

  useEffect(() => {
    loadVideo();
    progressTrackedRef.current = { 25: false, 50: false, 75: false, 100: false };
    watchTimeRef.current = 0;
    lastTimeRef.current = 0;
    completionSentRef.current = isAlreadyCompleted;
    lastPositionSaveRef.current = 0;
    setIsCompleted(isAlreadyCompleted);
  }, [courseId, topicId, isAlreadyCompleted]);

  const loadVideo = async () => {
    try {
      setLoading(true);
      setError(null);
      const request: TopicVideoRequest = { courseId, topicId };
      const response = await videoService.getTopicVideoUrl(request);
      setVideoUrl(response.video_url);
    } catch (err: any) {
      console.error('[TopicVideoPlayer] Error loading video:', err);
      trackError.videoError(videoUrl || 'unknown', err.message || 'Failed to load video', topicId);
      setError(err.message || 'Failed to load video');
    } finally {
      setLoading(false);
    }
  };

  const markTopicComplete = useCallback(async () => {
    if (!progressTrackingEnabled || completionSentRef.current) return;
    completionSentRef.current = true;
    setIsCompleted(true);

    try {
      const result = await progressService.markTopicComplete({
        enrollment_id: enrollmentId!,
        course_id: courseId,
        topic_id: topicId,
        segment_id: segmentId!,
        lesson_id: lessonId!,
        module_id: moduleId!,
        video_duration_seconds: videoRef.current?.duration || 0,
      });

      if (result.success && result.data) {
        onTopicCompleted?.(topicId, result.data.course_progress_percentage);
      } else {
        console.error('[TopicVideoPlayer] Failed:', result.error);
        completionSentRef.current = false;
        setIsCompleted(false);
      }
    } catch (err) {
      console.error('[TopicVideoPlayer] Error:', err);
      completionSentRef.current = false;
      setIsCompleted(false);
    }
  }, [progressTrackingEnabled, enrollmentId, courseId, topicId, segmentId, lessonId, moduleId, onTopicCompleted]);

  const saveVideoPosition = useCallback(async (currentTime: number, duration: number) => {
    if (!progressTrackingEnabled || completionSentRef.current) return;
    try {
      await progressService.updateVideoProgress({
        enrollment_id: enrollmentId!,
        course_id: courseId,
        topic_id: topicId,
        segment_id: segmentId!,
        lesson_id: lessonId!,
        module_id: moduleId!,
        current_position_seconds: currentTime,
        video_duration_seconds: duration,
      });
    } catch (err) {
      // Silent fail
    }
  }, [progressTrackingEnabled, enrollmentId, courseId, topicId, segmentId, lessonId, moduleId]);

  const handlePlay = () => {
    trackClassroom.videoPlay(topicId, videoUrl || '', courseId);
  };

  const handlePause = () => {
    if (videoRef.current) {
      trackClassroom.videoPause(topicId, videoRef.current.currentTime, videoRef.current.duration, courseId);
      saveVideoPosition(videoRef.current.currentTime, videoRef.current.duration);
    }
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const { currentTime, duration } = videoRef.current;
    if (!duration) return;

    watchTimeRef.current += currentTime - lastTimeRef.current;
    lastTimeRef.current = currentTime;

    const percent = Math.floor((currentTime / duration) * 100);

    const milestones = [25, 50, 75, 100] as const;
    for (const milestone of milestones) {
      if (percent >= milestone && !progressTrackedRef.current[milestone]) {
        trackClassroom.videoProgress(topicId, milestone, courseId);
        progressTrackedRef.current[milestone] = true;
      }
    }

    if (percent >= COMPLETION_THRESHOLD && !completionSentRef.current) {
      markTopicComplete();
    }

    const now = Date.now();
    if (now - lastPositionSaveRef.current > POSITION_SAVE_INTERVAL) {
      lastPositionSaveRef.current = now;
      saveVideoPosition(currentTime, duration);
    }
  };

  const handleEnded = () => {
    trackClassroom.videoComplete(topicId, watchTimeRef.current, courseId);
    if (!completionSentRef.current) {
      markTopicComplete();
    }
  };

  const handleError = () => {
    trackError.videoError(videoUrl || 'unknown', 'Video playback error', topicId);
  };

  if (loading) {
    return (
      <div className="w-full bg-slate-800 rounded-lg p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
        <p className="text-gray-400 mt-4">Loading video...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-red-900/20 border border-red-500 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <svg className="w-6 h-6 text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <p className="text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!videoUrl) {
    return null;
  }

  return (
    <div className="w-full bg-slate-900 rounded-lg overflow-hidden shadow-2xl">
      {isCompleted && (
        <div className="bg-green-600/20 border-b border-green-500/50 px-4 py-2 flex items-center gap-2">
          <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="text-green-400 text-sm font-medium">Topic Completed!</span>
        </div>
      )}
      <video
        ref={videoRef}
        className="w-full"
        controls
        autoPlay={autoplay}
        playsInline
        preload="metadata"
        onPlay={handlePlay}
        onPause={handlePause}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onError={handleError}
      >
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      {topicContent && (
        <div className="p-4 bg-slate-800 border-t border-slate-700">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-gray-400">{topicContent}</p>
          </div>
        </div>
      )}
    </div>
  );
};
